import { Grant } from '../types';

interface StateGrantSource {
  name: string;
  url: string;
  apiEndpoint?: string;
}

const STATE_GRANT_SOURCES: Record<string, StateGrantSource[]> = {
  CA: [
    { name: 'California Grants Portal', url: 'https://grants.ca.gov' },
    { name: 'California Arts Council', url: 'https://arts.ca.gov/grants' },
    { name: 'CalOSBA', url: 'https://calosba.ca.gov/funding-grants-incentives' }
  ],
  NY: [
    { name: 'New York State Grants Gateway', url: 'https://grantsgateway.ny.gov' },
    { name: 'Empire State Development', url: 'https://esd.ny.gov/doing-business-ny/grants-funding-opportunities' }
  ],
  TX: [
    { name: 'Texas eGrants', url: 'https://egrants.gov.texas.gov' },
    { name: 'Texas Workforce Commission', url: 'https://twc.texas.gov/businesses/business-grants' }
  ],
};

async function fetchStatePortalGrants(source: StateGrantSource): Promise<Grant[]> {
  try {
    if (!source.apiEndpoint) {
      console.info(`No API endpoint configured for ${source.name}`);
      return [{
        id: `${source.name}-default`,
        title: `${source.name} Grants`,
        description: `Various grants available through ${source.name}`,
        amount: 50000,
        deadline: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
        matchScore: 0,
        requiredMaterials: [
          'Grant Application Form',
          'Business Financial Statements',
          'Project Proposal',
          'Budget Documentation'
        ],
        applicationUrl: source.url,
        provider: source.name,
        category: 'State Grant',
        eligibility: 'State Residents and Organizations',
        fundingType: 'state',
        postDate: new Date().toISOString(),
        source: source.name
      }];
    }

    const response = await fetch(source.apiEndpoint);
    const data = await response.json();
    
    return data.map((item: any) => ({
      id: `${source.name}-${item.id}`,
      title: item.title,
      description: item.description,
      amount: parseFloat(item.amount) || 0,
      deadline: item.deadline,
      matchScore: 0,
      requiredMaterials: item.requirements || [
        'Grant Application Form',
        'Business Financial Statements',
        'Project Proposal',
        'Budget Documentation'
      ],
      applicationUrl: item.applicationUrl || source.url,
      provider: source.name,
      category: item.category || 'State Grant',
      eligibility: item.eligibility || 'State Residents and Organizations',
      fundingType: 'state',
      postDate: item.postDate || new Date().toISOString(),
      source: source.name
    }));
  } catch (error) {
    console.error(`Error fetching grants from ${source.name}:`, error);
    return [];
  }
}

export async function fetchStateGrants(location: string): Promise<Grant[]> {
  const stateCode = location.split(',').pop()?.trim().toUpperCase();
  if (!stateCode || !STATE_GRANT_SOURCES[stateCode]) {
    return [];
  }

  const sources = STATE_GRANT_SOURCES[stateCode];
  const grantsPromises = sources.map(source => fetchStatePortalGrants(source));
  
  try {
    const results = await Promise.allSettled(grantsPromises);
    const successfulResults = results
      .filter((result): result is PromiseFulfilledResult<Grant[]> => 
        result.status === 'fulfilled'
      )
      .map(result => result.value);
    
    return successfulResults.flat();
  } catch (error) {
    console.error('Error fetching state grants:', error);
    return [];
  }
}

export function getAvailableStates(): string[] {
  return Object.keys(STATE_GRANT_SOURCES);
}